/** @format */

import { useCallback, useEffect, useState } from "react";
import { useBrand } from "../../hooks/useBrand";
import { useSearch } from "../../hooks/useSearch";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { AdminQuestionFilters } from "../../components/AdminQuestionFilter";
import { AdminQuestionTable } from "../../components/AdminQuestionTable";
import type { QuestionStatus, SearchParams } from "@question-bank/shared";
import { useLocation, useSearchParams } from "react-router-dom";

const DEFAULT_LIMIT = 20;

/** Filter keys that carry plain string values when (de)serialised to the URL. */
const STRING_KEYS = [
	"q",
	"subject",
	"level",
	"language",
	"tier",
	"specification",
	"specificationTheme",
	"specificationSubTopic",
	"assessmentObjective",
	"paperCode",
] as const;

/** Build a `SearchParams` object from the current URL query string. */
const filtersFromSearchParams = (params: URLSearchParams): SearchParams => {
	// Admin views every status by default; the status filter narrows it.
	const filters: SearchParams = { limit: DEFAULT_LIMIT, offset: 0, includeDrafts: true };

	for (const key of STRING_KEYS) {
		const value = params.get(key);
		if (value) {
			filters[key] = value;
		}
	}

	const season = params.get("season");
	if (season === "Summer" || season === "Winter") {
		filters.season = season;
	}

	const status = params.get("status");
	if (status) {
		filters.status = status as QuestionStatus;
	}

	const year = Number(params.get("year"));
	if (Number.isFinite(year) && year > 0) {
		filters.year = year;
	}

	const limit = Number(params.get("limit"));
	if (Number.isFinite(limit) && limit > 0) {
		filters.limit = limit;
	}

	const offset = Number(params.get("offset"));
	if (Number.isFinite(offset) && offset > 0) {
		filters.offset = offset;
	}

	if (params.get("includeDeleted") === "true") {
		filters.includeDeleted = true;
	}

	return filters;
};

/** Serialise active filters into a `URLSearchParams`, omitting empty/default values. */
const searchParamsFromFilters = (filters: SearchParams): URLSearchParams => {
	const params = new URLSearchParams();

	for (const [key, value] of Object.entries(filters)) {
		if (value === undefined || value === null || value === "") {
			continue;
		}
		// Keep the URL clean by omitting defaults that add no information.
		if (key === "limit" && value === DEFAULT_LIMIT) {
			continue;
		}
		if (key === "offset" && value === 0) {
			continue;
		}
		params.set(key, String(value));
	}

	return params;
};

export const AdminQuestionsPage = () => {
	useDocumentTitle("Admin – Questions");
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { brand } = useBrand();
	const { results, isLoading, error, search } = useSearch(brand);
	const [filters, setFilters] = useState<SearchParams>(() => {
		// URL query params win so shared links are honoured; fall back to
		// router state (e.g. returning from a detail page), then defaults.
		if (Array.from(searchParams.keys()).length > 0) {
			return filtersFromSearchParams(searchParams);
		}
		return (
			(location.state as { filters?: SearchParams } | null)?.filters ?? {
				limit: DEFAULT_LIMIT,
				offset: 0,
				includeDrafts: true,
			}
		);
	});

	useEffect(() => {
		void search(filters);
	}, [filters, search]);

	// Keep the URL in sync so admins can copy/share the current view.
	useEffect(() => {
		setSearchParams(searchParamsFromFilters(filters), { replace: true });
	}, [filters, setSearchParams]);

	const handleFilterChange = useCallback((updated: Partial<SearchParams>) => {
		setFilters((prev) => ({ ...prev, ...updated, offset: 0 }));
	}, []);

	const handlePageChange = useCallback((offset: number) => {
		setFilters((prev) => ({ ...prev, offset }));
	}, []);

	const handleRetry = useCallback(() => {
		void search(filters);
	}, [search, filters]);

	return (
		<section className="space-y-6">
			<div>
				<h2 className="text-[1.75rem] font-bold text-(--admin-color-primary)">Questions </h2>
				<p className="mt-2 text-sm text-slate-600">Browse and manage questions</p>
			</div>

			<div className="flex flex-col gap-6">
				<AdminQuestionFilters aggregations={results?.aggregations} filters={filters} onChange={handleFilterChange} />

				<div className="min-w-0 flex-1">
					<AdminQuestionTable
						questions={results?.data ?? []}
						total={results?.meta.total ?? 0}
						limit={results?.meta.limit ?? DEFAULT_LIMIT}
						offset={results?.meta.offset ?? 0}
						isLoading={isLoading}
						error={error}
						filters={filters}
						onPageChange={handlePageChange}
						onRetry={handleRetry}
					/>
				</div>
			</div>
		</section>
	);
};
